import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Upload, X, Sparkles, RotateCcw, Download, Ruler, HandHelping, Share2, Shuffle, Film } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SketchCanvas from "@/components/SketchCanvas";
import FabricPicker from "@/components/FabricPicker";
import VoiceRecorder from "@/components/VoiceRecorder";
import TechPackModal from "@/components/TechPackModal";
import ShareModal from "@/components/ShareModal";
import RunwayModal from "@/components/RunwayModal";

const CATEGORIES = [
  { id: "clothing", label: "Clothing" },
  { id: "footwear", label: "Footwear" },
  { id: "accessory", label: "Accessory" },
];

const SILHOUETTES = {
  clothing: ["A-line", "Bodycon", "Oversized", "Cropped", "Draped", "Structured", "Deconstructed"],
  footwear: ["Sneaker", "Boot", "Heel", "Mule", "Sandal", "Loafer", "Platform"],
  accessory: ["Bag", "Hat", "Belt", "Sunglasses", "Jewelry", "Scarf", "Gloves"],
};

const LENGTHS = ["Micro", "Mini", "Knee", "Midi", "Ankle", "Floor", "Train"];

export default function Studio() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [category, setCategory] = useState("clothing");
  const [prompt, setPrompt] = useState("");
  const [fabric, setFabric] = useState(null);
  const [silhouette, setSilhouette] = useState(null);
  const [length, setLength] = useState(null);
  const [color, setColor] = useState("");
  const [refImage, setRefImage] = useState(null); // { dataUrl, base64 }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showTechPack, setShowTechPack] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showRunway, setShowRunway] = useState(false);
  const [varying, setVarying] = useState(false);
  const sketchRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const handleUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setRefImage({ dataUrl, base64: dataUrl.split(",")[1] });
    };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (!prompt.trim() && !sketchRef.current?.hasContent() && !refImage) {
      toast.error("Describe, sketch, or upload something first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const sketchB64 = sketchRef.current?.hasContent() ? sketchRef.current.getBase64() : null;
      // Prefer reference photo; if none but sketch exists, use sketch as reference
      const referenceB64 = refImage?.base64 || sketchB64;
      const finalPrompt = [
        prompt || "",
        sketchB64 && !refImage ? "Interpret and refine the accompanying hand sketch as the silhouette blueprint." : "",
        refImage && sketchB64 ? "Use the attached photo as mood; keep proportions faithful to my hand sketch below." : "",
      ].filter(Boolean).join(" ");

      const { data } = await api.post("/design/generate", {
        prompt: finalPrompt || "Design an editorial piece.",
        category,
        fabric,
        silhouette,
        length,
        color: color || null,
        reference_image_b64: referenceB64,
      });
      setResult(data);
      toast.success("Design rendered");
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error(e);
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const startNew = () => {
    setResult(null);
    sketchRef.current?.clear();
    setRefImage(null);
    setPrompt("");
  };

  const vary = async () => {
    if (!result) return;
    setVarying(true);
    try {
      const { data } = await api.post(`/designs/${result.id}/vary`);
      setResult(data);
      toast.success("Variation rendered");
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      toast.error(err?.response?.data?.detail || "Variation failed");
    } finally { setVarying(false); }
  };

  const silhouetteOptions = SILHOUETTES[category];

  if (loading) return <div className="min-h-screen bg-zinc-950" />;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pt-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-[calc(100vh-4rem)]">
        {/* CANVAS AREA */}
        <div className="lg:col-span-8 relative p-4 md:p-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="overline text-zinc-500">InstaLux · Design N°{new Date().getTime().toString().slice(-4)}</div>
              <h1 className="font-serif text-3xl md:text-4xl mt-1">The Cutting Table</h1>
            </div>
            {result && (
              <button onClick={startNew} className="overline text-zinc-400 hover:text-lime-400 flex items-center gap-2 transition-colors" data-testid="studio-new-btn">
                <RotateCcw className="w-3.5 h-3.5" /> New piece
              </button>
            )}
          </div>

          <div className="relative w-full h-[65vh] lg:h-[calc(100vh-16rem)]">
            {result ? (
              <div className="relative w-full h-full bg-zinc-900 border border-zinc-800 overflow-hidden reveal" data-testid="studio-result-view">
                <img src={fileUrl(result.image_path)} alt={result.title} className="absolute inset-0 w-full h-full object-contain" />
                <div className="absolute top-4 left-4 backdrop-blur-xl bg-zinc-950/60 border border-white/10 px-3 py-2">
                  <div className="overline text-lime-400">{result.category}</div>
                  <div className="font-serif italic text-xl leading-tight">{result.title}</div>
                </div>
                <a
                  href={fileUrl(result.image_path)}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="absolute bottom-4 right-4 backdrop-blur-xl bg-zinc-950/60 border border-white/10 px-4 py-2 overline flex items-center gap-2 hover:text-lime-400"
                  data-testid="studio-download-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <div className="absolute bottom-4 left-4 flex flex-wrap gap-2 max-w-[calc(100%-2rem)]">
                  <button
                    onClick={() => setShowTechPack(true)}
                    className="backdrop-blur-xl bg-lime-400 text-zinc-950 px-4 py-2 overline flex items-center gap-2 hover:bg-zinc-950 hover:text-lime-400 transition-colors"
                    data-testid="studio-techpack-btn"
                  >
                    <Ruler className="w-3.5 h-3.5" /> Tech Pack
                  </button>
                  <button
                    onClick={vary}
                    disabled={varying}
                    className="backdrop-blur-xl bg-zinc-950/60 text-zinc-50 border border-white/10 px-4 py-2 overline flex items-center gap-2 hover:text-lime-400 transition-colors disabled:opacity-60"
                    data-testid="studio-vary-btn"
                  >
                    {varying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Varying</> : <><Shuffle className="w-3.5 h-3.5" /> Vary this</>}
                  </button>
                  <button
                    onClick={() => setShowShare(true)}
                    className="backdrop-blur-xl bg-zinc-950/60 text-zinc-50 border border-white/10 px-4 py-2 overline flex items-center gap-2 hover:text-lime-400 transition-colors"
                    data-testid="studio-share-btn"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <button
                    onClick={() => setShowRunway(true)}
                    className="backdrop-blur-xl bg-zinc-950/60 text-zinc-50 border border-white/10 px-4 py-2 overline flex items-center gap-2 hover:text-lime-400 transition-colors"
                    data-testid="studio-runway-btn"
                  >
                    <Film className="w-3.5 h-3.5" /> Runway
                  </button>
                  <button
                    onClick={() => navigate(`/makers?design=${result.id}`)}
                    className="backdrop-blur-xl bg-zinc-950/60 text-zinc-50 border border-white/10 px-4 py-2 overline flex items-center gap-2 hover:text-lime-400 transition-colors"
                    data-testid="studio-find-maker-btn"
                  >
                    <HandHelping className="w-3.5 h-3.5" /> Find a Maker
                  </button>
                </div>
              </div>
            ) : (
              <SketchCanvas ref={sketchRef} refImageDataUrl={refImage?.dataUrl} />
            )}

            {busy && (
              <div className="absolute inset-0 backdrop-blur-md bg-zinc-950/70 flex flex-col items-center justify-center z-30" data-testid="studio-loading-overlay">
                <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
                <div className="overline text-lime-400 mt-6">Rendering</div>
                <div className="font-serif italic text-3xl mt-2">Cutting the pattern…</div>
              </div>
            )}
          </div>
        </div>

        {/* TOOLS SIDEBAR */}
        <aside className="lg:col-span-4 border-l border-zinc-800 bg-zinc-950 p-6 md:p-8 overflow-y-auto max-h-[calc(100vh-4rem)]" data-testid="studio-tools-panel">
          {/* Category */}
          <div className="mb-6">
            <div className="overline text-zinc-500 mb-3">Category</div>
            <div className="grid grid-cols-3 gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setCategory(c.id); setSilhouette(null); }}
                  className={`py-3 overline border ${
                    category === c.id ? "bg-lime-400 text-zinc-950 border-lime-400" : "border-zinc-800 text-zinc-400 hover:border-lime-400"
                  } transition-colors`}
                  data-testid={`studio-category-${c.id}`}
                >{c.label}</button>
              ))}
            </div>
          </div>

          {/* Prompt */}
          <div className="mb-6">
            <div className="overline text-zinc-500 mb-3 flex items-center justify-between">
              <span>Instruction</span>
              <VoiceRecorder onTranscript={(t) => setPrompt((p) => (p ? p + " " : "") + t)} />
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="A bias-cut satin gown, cinched ankle, no straps, molten silver…"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-lime-400 focus:outline-none p-3 font-serif italic text-lg leading-snug placeholder:text-zinc-600 resize-none"
              data-testid="studio-prompt-input"
            />
          </div>

          {/* Fabric */}
          <div className="mb-6">
            <div className="overline text-zinc-500 mb-3">Fabric {fabric && <span className="text-lime-400 not-italic normal-case tracking-normal font-serif italic ml-1">— {fabric}</span>}</div>
            <FabricPicker value={fabric} onChange={setFabric} />
          </div>

          {/* Silhouette */}
          <div className="mb-6">
            <div className="overline text-zinc-500 mb-3">Silhouette</div>
            <div className="flex flex-wrap gap-1">
              {silhouetteOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => setSilhouette(silhouette === s ? null : s)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${
                    silhouette === s ? "bg-lime-400 text-zinc-950 border-lime-400" : "border-zinc-800 text-zinc-400 hover:border-lime-400"
                  } transition-colors`}
                  data-testid={`studio-silhouette-${s.toLowerCase()}`}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Length */}
          {category === "clothing" && (
            <div className="mb-6">
              <div className="overline text-zinc-500 mb-3">Length {length && <span className="ml-1 text-lime-400 font-serif italic normal-case tracking-normal">— {length}</span>}</div>
              <div className="flex gap-0.5">
                {LENGTHS.map((l, i) => (
                  <button
                    key={l}
                    onClick={() => setLength(length === l ? null : l)}
                    className={`flex-1 py-2 text-[10px] uppercase tracking-widest border ${
                      length === l ? "bg-lime-400 text-zinc-950 border-lime-400" : "border-zinc-800 text-zinc-400 hover:border-lime-400"
                    } transition-colors`}
                    data-testid={`studio-length-${l.toLowerCase()}`}
                  >{l}</button>
                ))}
              </div>
            </div>
          )}

          {/* Color */}
          <div className="mb-6">
            <div className="overline text-zinc-500 mb-3">Color / Palette</div>
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="e.g. molten silver + ivory"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-lime-400 focus:outline-none p-3 text-sm placeholder:text-zinc-600"
              data-testid="studio-color-input"
            />
          </div>

          {/* Reference */}
          <div className="mb-8">
            <div className="overline text-zinc-500 mb-3">Reference</div>
            {refImage ? (
              <div className="relative border border-zinc-800 aspect-video overflow-hidden">
                <img src={refImage.dataUrl} alt="ref" className="absolute inset-0 w-full h-full object-cover" />
                <button
                  onClick={() => setRefImage(null)}
                  className="absolute top-2 right-2 w-8 h-8 bg-zinc-950/80 backdrop-blur border border-white/10 flex items-center justify-center hover:text-rose-400"
                  data-testid="studio-remove-ref-btn"
                ><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border border-dashed border-zinc-700 hover:border-lime-400 hover:text-lime-400 py-8 flex flex-col items-center gap-2 text-zinc-500 transition-colors"
                data-testid="studio-upload-ref-btn"
              >
                <Upload className="w-5 h-5" />
                <span className="overline">Drop sketch / photo / video</span>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={handleUpload} data-testid="studio-file-input" />
          </div>

          {/* Generate */}
          <button
            onClick={generate}
            disabled={busy}
            className={`w-full py-4 rounded-full flex items-center justify-center gap-2 overline ${
              busy ? "bg-zinc-800 text-zinc-500 cursor-not-allowed" : "bg-lime-400 text-zinc-950 hover:bg-lime-300"
            } transition-colors`}
            data-testid="studio-generate-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering</> : <><Sparkles className="w-4 h-4" /> Render Design</>}
          </button>
        </aside>
      </div>

      {showTechPack && result && (
        <TechPackModal design={result} techpack={null} onClose={() => setShowTechPack(false)} />
      )}
      {showShare && result && (
        <ShareModal design={result} onClose={() => setShowShare(false)} onUpdate={(d) => setResult(d)} />
      )}
      {showRunway && result && (
        <RunwayModal design={result} onClose={() => setShowRunway(false)} onUpdate={(d) => setResult(d)} />
      )}
    </div>
  );
}
